import React, { useState, useEffect } from 'react';
import { Container, CssBaseline, ThemeProvider, createTheme, Box, Typography, Button } from '@mui/material';
import axios from 'axios';
import SearchForm from './Components/SearchForm';
import SearchResults from './Components/SearchResults';
import AfiliadoInfo from './Components/AfiliadoInfo';
import FamilyMembers from './Components/FamilyMembers';
import PaymentInfo from './Components/PaymentInfo';
import LoginForm from './Components/LoginForm';
import LoginIcon from '@mui/icons-material/Login';
import CtaCteDialog from './Components/CtaCteDialog';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
  },
});

function App() {
  const [afiliado, setAfiliado] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentError, setPaymentError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [isCtaCteOpen, setIsCtaCteOpen] = useState(false);
  const [ctaCteIdAfiliado, setCtaCteIdAfiliado] = useState(null);

  const handleVerCtaCte = (idAfiliado) => {
    setCtaCteIdAfiliado(idAfiliado);
    setIsCtaCteOpen(true);
  };

  useEffect(() => {
    // Check if user is already authenticated
    const token = localStorage.getItem('authToken');
    if (token) {
      setAuthToken(token);
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (username, password) => {
    try {
      setLoginError(null);
      const response = await axios.post('/api/login', {
        username,
        password
      });
      
      if (response.data.success) {
        const token = response.data.token;
        setAuthToken(token);
        setIsAuthenticated(true);
        localStorage.setItem('authToken', token);
      }
    } catch (err) {
      setLoginError(err.response?.data?.message || 'Error de conexión');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthToken(null);
    setAfiliado(null);
    setSearchResults(null);
    localStorage.removeItem('authToken');
  };

  const clearResults = () => {
    setAfiliado(null);
    setSearchResults(null);
    setError(null);
    setPaymentError(null);
  };

  const handleSearch = async (dni) => {
    setLoading(true);
    clearResults();
    
    try {
      const response = await axios.get(`/api/afiliado/${dni}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      setAfiliado(response.data);
      
      // Check for payment information in the response
      if (response.data.error) {
        setPaymentError(response.data.error);
      }
      
    } catch (err) {
      if (err.response?.status === 401) {
        // Token expired or invalid, logout user
        handleLogout();
        return;
      }
      
      if (err.response) {
        console.error('Error response:', err.response.data);
        setError({
          message: err.response.data.error || 'Error del servidor',
          details: err.response.data.details || 'Ocurrió un error inesperado.',
          similar_dnis: err.response.data.similar_dnis || []
        });
      } else if (err.request) {
        console.error('No response received:', err.request);
        setError({
          message: 'No se pudo conectar con el servidor',
          details: 'Verifique su conexión a internet e intente nuevamente.'
        });
      } else {
        console.error('Error:', err.message);
        setError({
          message: 'Error en la aplicación',
          details: err.message
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNameSearch = async (searchData) => {
    setLoading(true);
    clearResults();
    
    try {
      const response = await axios.post('/api/afiliado/buscar', searchData, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSearchResults(response.data.resultados);
        if (response.data.resultados.length === 0) {
          setError({
            message: 'No se encontraron afiliados',
            details: `No se encontraron afiliados que coincidan con los criterios de búsqueda.`
          });
        }
      } else {
        setError({
          message: 'Error en la búsqueda',
          details: response.data.error || 'Error desconocido'
        });
      }
      
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        return;
      }
      
      if (err.response) {
        console.error('Error response:', err.response.data);
        setError({
          message: err.response.data.error || 'Error del servidor',
          details: err.response.data.details || 'Ocurrió un error inesperado.'
        });
      } else if (err.request) {
        console.error('No response received:', err.request);
        setError({
          message: 'No se pudo conectar con el servidor',
          details: 'Verifique su conexión a internet e intente nuevamente.'
        });
      } else {
        console.error('Error:', err.message);
        setError({
          message: 'Error en la aplicación',
          details: err.message
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectFromResults = (dni) => {
    // Clear search results and search for the specific affiliate
    setSearchResults(null);
    handleSearch(dni);
  };

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginForm onLogin={handleLogin} error={loginError} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h4" component="h1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              Sistema de Gestión de Afiliados
            </Typography>
            <Button 
              variant="outlined" 
              onClick={handleLogout}
              startIcon={<LoginIcon />}
            >
              Cerrar Sesión
            </Button>
          </Box>
          
          <SearchForm 
            onSearch={handleSearch} 
            onNameSearch={handleNameSearch}
            loading={loading} 
            error={error} 
          />
          
          {searchResults && (
            <SearchResults 
              results={searchResults} 
              onSelectAfiliado={handleSelectFromResults}
            />
          )}
          
          {afiliado && (
            <>
              <AfiliadoInfo afiliado={afiliado} onVerCtaCte={handleVerCtaCte} />
              <FamilyMembers familiares={afiliado.familiares || []} />
              <PaymentInfo 
                pagos={afiliado.pagos} 
                error={paymentError || (afiliado.error && afiliado.error.includes('pagos') ? afiliado.error : null)} 
              />
            </>
          )}
          
          <Box sx={{ mt: 4, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">
              {new Date().getFullYear()} Sistema de Gestión de Afiliados
            </Typography>
          </Box>
          <CtaCteDialog 
            open={isCtaCteOpen} 
            onClose={() => setIsCtaCteOpen(false)} 
            idAfiliado={ctaCteIdAfiliado} 
            authToken={authToken} 
          />
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
